// "Seus gritos" no Perfil: gravar TRUCO, SEIS, NOVE e DOZE com a própria voz (planos Prata e Ouro),
// com consentimento explícito para a voz tocar na mesa (LGPD).
import { useRef, useState } from 'react';
import type { Grito } from '@truco6/mesa/protocolo';
import { planosQueTem, pode } from '@truco6/planos';
import { conta, type PerfilConta, type Sessao } from '../comum/conta.ts';
import { ir } from '../comum/rotas.ts';
import { GRITOS, falar } from '../som/gritos.ts';
import { DURACAO_GRITO, gravar, podeGravar } from '../som/gravador.ts';

const ROTULO: Record<Grito, string> = { truco: 'TRUCO!', seis: 'SEIS!', nove: 'NOVE!', doze: 'DOZE!' };

type Etapa = { grito: Grito; fase: 'gravando' } | { grito: Grito; fase: 'ouvindo'; audio: Blob; url: string } | null;

export function Gritos({ sessao, perfil, aoMudar }: { sessao: Sessao; perfil: PerfilConta; aoMudar: (p: PerfilConta) => void }) {
  const [etapa, setEtapa] = useState<Etapa>(null);
  const [ocupado, setOcupado] = useState(false);
  const [aceite, setAceite] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const tocando = useRef<HTMLAudioElement | null>(null);

  const temPlano = pode(perfil.plano, 'gravarGritos');
  const tocar = (url: string) => { tocando.current?.pause(); const a = new Audio(url); tocando.current = a; void a.play().catch(() => {}); };
  async function fazer(fn: () => Promise<void>) {
    setErro(null); setOk(null); setOcupado(true);
    try { await fn(); } catch (e) { setErro((e as Error).message || 'Não deu certo. Tente de novo.'); } finally { setOcupado(false); }
  }

  let corpo: React.ReactNode;
  if (sessao.anonimo) {
    corpo = <p className="cadeado">Guarde a conta com Google para gravar seus gritos. Disponível nos planos {planosQueTem('gravarGritos')}.</p>;
  } else if (!temPlano) {
    corpo = (
      <>
        <p className="cadeado">Disponível nos planos {planosQueTem('gravarGritos')}. Na mesa, você grita com a voz padrão.</p>
        <button className="btn2 dourado" onClick={() => ir({ tela: 'planos', plano: 'prata', ciclo: 'mensal' })}>Ver planos</button>
      </>
    );
  } else if (!podeGravar()) {
    corpo = <p className="cadeado">Este navegador não grava áudio. Tente pelo Chrome ou pelo Safari atualizado.</p>;
  } else if (!perfil.vozConsentidaEm) {
    corpo = (
      <>
        <label className="aceite">
          <input type="checkbox" checked={aceite} onChange={(e) => setAceite(e.target.checked)} />
          <span>Autorizo que minha voz toque para quem joga comigo. Posso apagar quando quiser.</span>
        </label>
        <button className="btn2" disabled={!aceite || ocupado} onClick={() => fazer(async () => {
          const quando = await conta.consentirVoz(sessao, true);
          aoMudar({ ...perfil, vozConsentidaEm: quando });
        })}>Autorizar e gravar</button>
      </>
    );
  } else {
    corpo = (
      <>
        <ul className="gritos">
          {GRITOS.map((g) => {
            const versao = perfil.gritos[g], url = versao ? conta.urlGrito(sessao.id, g, versao) : null;
            const aqui = etapa?.grito === g ? etapa : null;
            return (
              <li key={g}>
                <b>{ROTULO[g]}</b>
                {aqui?.fase === 'gravando' ? (
                  <span className="gravando" role="status">Gravando... grite agora!</span>
                ) : aqui?.fase === 'ouvindo' ? (
                  <span className="acoes">
                    <button className="btn2" onClick={() => tocar(aqui.url)}>Ouvir</button>
                    <button className="btn1 pequeno" disabled={ocupado} onClick={() => fazer(async () => {
                      const gritos = await conta.enviarGrito(sessao, g, aqui.audio, perfil.gritos);
                      aoMudar({ ...perfil, gritos });
                      setEtapa(null);
                      setOk(ROTULO[g] + ' salvo.');
                    })}>SALVAR</button>
                    <button className="btn2" disabled={ocupado} onClick={() => setEtapa(null)}>Descartar</button>
                  </span>
                ) : (
                  <span className="acoes">
                    <button className="btn2" aria-label={'Ouvir ' + ROTULO[g]} onClick={() => (url ? tocar(url) : falar(g, 0))}>
                      {url ? 'Ouvir' : 'Voz padrão'}
                    </button>
                    <button className="btn2" disabled={ocupado || etapa !== null} aria-label={(versao ? 'Regravar ' : 'Gravar ') + ROTULO[g]}
                      onClick={() => fazer(async () => {
                        setEtapa({ grito: g, fase: 'gravando' });
                        try {
                          const audio = await gravar(DURACAO_GRITO);
                          const u = URL.createObjectURL(audio);
                          setEtapa({ grito: g, fase: 'ouvindo', audio, url: u });
                          tocar(u);
                        } catch (e) { setEtapa(null); throw e; }
                      })}>{versao ? 'Regravar' : 'Gravar'}</button>
                    {versao && (
                      <button className="btn2" disabled={ocupado} aria-label={'Apagar ' + ROTULO[g]} onClick={() => fazer(async () => {
                        aoMudar({ ...perfil, gritos: await conta.apagarGrito(sessao, g, perfil.gritos) });
                      })}>Apagar</button>
                    )}
                  </span>
                )}
              </li>
            );
          })}
        </ul>
        <p className="nota">Cada grito tem até {DURACAO_GRITO / 1000} s. Quem joga com você pode desligar as vozes gravadas no som da mesa.</p>
        <button className="link" disabled={ocupado} onClick={() => fazer(async () => {
          const quando = await conta.consentirVoz(sessao, false);
          aoMudar({ ...perfil, vozConsentidaEm: quando, gritos: {} });
          setOk('Autorização retirada. Seus gritos foram apagados.');
        })}>Retirar autorização e apagar meus gritos</button>
      </>
    );
  }

  return (
    <section className="cartao" aria-labelledby="h-gritos">
      <h2 id="h-gritos" className="titulo-cartao">Seus gritos</h2>
      <p>Grite TRUCO, SEIS, NOVE e DOZE com a sua voz. Na mesa, todo mundo ouve você pedir.</p>
      {corpo}
      {erro && <p className="erro" role="alert">{erro}</p>}
      {ok && <p className="ok" role="status">{ok}</p>}
    </section>
  );
}
